import type { AnonCart, AnonCartContext } from "../model/AnonCartTypes";
import type { AnonCartEvent } from "../stores/anon-cart/AnonCartEvents";
import { Request } from "../stores/Requests20";
import { url as encodeUrl } from "../utils/UrlUtil";
import { parseServerEvent } from "../stores/anon-cart/AnonCartEvents";
import { handleServiceResponse } from "./ServerRequestsInternal";
const BASE_URL = "/ordercapture/anonymouscart/anon-carts";
const TIMEOUT = 5000;
type GetCartIdRequest = {
	cartContext: AnonCartContext;
};
type GetCartRequest = {
	cartContext: AnonCartContext;
	cartId: string;
};
type GetCartEventsRequest = {
	cartId: string;
	eventIds?: Array<string>;
};
type CreateCartRequest = {
	requestId: string;
	cartContext: AnonCartContext;
};
type DeleteCartRequest = {
	requestId: string;
	cartContext: AnonCartContext;
	cartId: string;
};
type AddItemRequest = {
	requestId: string;
	cartContext: AnonCartContext;
	cartId: string;
	bundleId: string;
	quantity: number;
};
type DeleteItemRequest = {
	requestId: string;
	cartId: string;
	cartContext: AnonCartContext;
	bundleId: string;
};
type SetItemQuantityRequest = {
	requestId: string;
	cartContext: AnonCartContext;
	cartId: string;
	bundleId: string;
	quantity: number;
};
type SetItemServiceOptionsRequest = {
	requestId: string;
	cartContext: AnonCartContext;
	cartId: string;
	bundleId: string;
	serviceOptionIds: Array<string>;
};
type MarkCartMergedRequest = {
	requestId: string;
	cartContext: AnonCartContext;
	cartId: string;
	targetCartId: string;
};
export function getCartId(params: GetCartIdRequest): Promise<string | null | undefined> {
	const url = `${BASE_URL}?${getQueryParams(params.cartContext)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return handleServiceResponse(response).then((json) => json.data);
}
export function getCart(params: GetCartRequest): Promise<AnonCart | null | undefined> {
	const url = `${BASE_URL}/${params.cartId}?${getQueryParams(params.cartContext)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return handleServiceResponse(response).then((json) => json.data);
}
export async function getEvents(params: GetCartEventsRequest): Promise<AnonCartEvent[]> {
	const url = `${BASE_URL}/${params.cartId}/events?${getQueryParamsFrom(params.eventIds)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	const json = await handleServiceResponse(response);
	return json.data.events.map((event) => parseServerEvent(event));
}
export function createCart(params: CreateCartRequest): Promise<string> {
	const url = `${BASE_URL}`;
	const cartContext: AnonCartContext = params.cartContext;
	const requestBody = {
		requestId: params.requestId,
		country: cartContext.country,
		storeId: cartContext.storeId,
		anonUserId: cartContext.anonUserId,
	};
	const response = Request.post(url).timeout(TIMEOUT).execute(requestBody);
	return handleServiceResponse(response).then((json) => json.data);
}
export async function deleteCart(params: DeleteCartRequest): Promise<void> {
	const url =
		`${BASE_URL}/${params.cartId}?${getQueryParams(params.cartContext)}` +
		encodeUrl`&requestId=${params.requestId}`;
	const response = Request.delete(url).timeout(TIMEOUT).execute();
	await handleServiceResponse(response);
}
export async function markCartMerged(params: MarkCartMergedRequest): Promise<void> {
	const url = `${BASE_URL}/${params.cartId}/merge?${getQueryParams(params.cartContext)}`;
	const requestBody = {
		requestId: params.requestId,
		targetCartId: params.targetCartId,
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	await handleServiceResponse(response);
}
export async function addItem(params: AddItemRequest): Promise<void> {
	const url = `${BASE_URL}/${params.cartId}/items?${getQueryParams(params.cartContext)}`;
	const requestBody = {
		requestId: params.requestId,
		bundleId: params.bundleId,
		quantity: params.quantity,
	};
	const response = Request.post(url).timeout(TIMEOUT).execute(requestBody);
	await handleServiceResponse(response);
}
export async function setItemQuantity(params: SetItemQuantityRequest): Promise<void> {
	const url = `${BASE_URL}/${params.cartId}/items/${params.bundleId}/quantity?${getQueryParams(params.cartContext)}`;
	const requestBody = {
		requestId: params.requestId,
		bundleId: params.bundleId,
		quantity: params.quantity,
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	await handleServiceResponse(response);
}
export async function setItemServiceOptions(params: SetItemServiceOptionsRequest): Promise<void> {
	const url = `${BASE_URL}/${params.cartId}/items/${params.bundleId}/service-options?${getQueryParams(
		params.cartContext,
	)}`;
	const requestBody = {
		requestId: params.requestId,
		serviceOptionIds: params.serviceOptionIds,
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	await handleServiceResponse(response);
}
export async function deleteItem(params: DeleteItemRequest): Promise<void> {
	const url =
		`${BASE_URL}/${params.cartId}/items/${params.bundleId}?${getQueryParams(params.cartContext)}` +
		encodeUrl`&requestId=${params.requestId}`;
	const response = Request.delete(url).timeout(TIMEOUT).execute();
	await handleServiceResponse(response);
}
export function getCartVersion(params: GetCartRequest): Promise<number | null | undefined> {
	const url = `${BASE_URL}/${params.cartId}/version?${getQueryParams(params.cartContext)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return handleServiceResponse(response).then((json) => json.data);
}

function getQueryParams(cartContext: AnonCartContext): string {
	return encodeUrl`country=${cartContext.country}&storeId=${cartContext.storeId}&anonUserId=${cartContext.anonUserId}`;
}

function getQueryParamsFrom(eventIds?: Array<string>): string {
	if (!eventIds) {
		return "";
	}

	return eventIds.map((id) => "eventIds=" + encodeUrl`${id}`).join("&");
}
